import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useLang, track } from "@/context/LanguageContext";
import { SectionLabel } from "@/components/Editorial";
import { PageHead } from "@/components/Shared";
import { CONTACT } from "@/data/config";

import { API } from "@/lib/api";

export default function Contact() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const isPrivate = params.get("topic") === "private";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: isPrivate ? t.contact.privatePrefill : "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      track("contact_submit");
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success(t.contact.success);
    } catch (err) {
      setStatus("error");
      toast.error(t.contact.error);
    }
  };

  const field = "w-full border-b border-onyx/25 bg-transparent py-4 font-sans text-lg text-onyx placeholder:text-onyx/40 focus:border-onyx focus:outline-none transition-colors";

  return (
    <>
      <Toaster position="top-center" richColors />
      <PageHead label={t.contact.label} title={t.contact.title} lead={t.contact.body} />

      <section className="bg-petal" data-testid="contact-body">
        <div className="mx-auto grid max-w-[1600px] gap-16 px-6 py-16 md:grid-cols-12 md:px-12 md:py-24">
          <div className="md:col-span-7">
            {isPrivate && (
              <div className="mb-8 inline-flex items-center gap-3 border border-onyx/20 px-5 py-3" data-testid="contact-topic-private">
                <span className="h-1.5 w-1.5 bg-terracotta" />
                <span className="label text-onyx">{t.contact.privateTopic}</span>
              </div>
            )}
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid gap-8 md:grid-cols-2">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.contact.name} className={field} data-testid="contact-name" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.contact.email} className={field} data-testid="contact-email" />
              </div>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.contact.phone} className={field} data-testid="contact-phone" />
              <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.contact.message} className={`${field} resize-none`} data-testid="contact-message" />
              <button type="submit" disabled={status === "sending"} className="label border border-onyx bg-onyx px-10 py-5 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx disabled:opacity-50" data-testid="contact-submit">
                {status === "sending" ? t.contact.sending : t.contact.submit}
              </button>
              {status === "success" && <p className="label text-terracotta" data-testid="contact-success">{t.contact.success}</p>}
            </form>
          </div>

          <div className="md:col-span-4 md:col-start-9">
            <SectionLabel>{t.contact.findUs}</SectionLabel>
            <div className="mt-6 space-y-2 font-sans text-base text-onyx/75">
              <p className="font-serif text-2xl text-onyx">Maison Core</p>
              <p>{CONTACT.city}</p>
              <p className="text-onyx/60">{CONTACT.address}</p>
              <p><a href={`mailto:${CONTACT.email}`} className="border-b border-onyx/30 hover:border-onyx">{CONTACT.email}</a></p>
              <p><a href={`tel:${CONTACT.phoneRaw}`} className="border-b border-onyx/30 hover:border-onyx">{CONTACT.phone}</a></p>
            </div>
            <div className="mt-6 border-t border-onyx/15 pt-6">
              <p className="label text-terracotta">{t.contact.hours}</p>
              <ul className="mt-4 space-y-1.5 font-sans text-sm text-onyx/75" data-testid="contact-hours">
                {CONTACT.hours.map((row) => (
                  <li key={row.h} className="flex justify-between gap-6">
                    <span>{row[lang]}</span>
                    <span className="text-onyx/55">{row.h}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href={`${CONTACT.whatsapp}?text=${encodeURIComponent(t.contact.whatsappPrefill)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { source: "contact" })}
                className="label w-fit border border-onyx bg-onyx px-8 py-4 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx"
                data-testid="contact-whatsapp"
              >
                {t.contact.whatsapp} →
              </a>
              <a href={CONTACT.mapsUrl} target="_blank" rel="noopener noreferrer" className="label border-b border-onyx pb-1 w-fit hover:opacity-60" data-testid="contact-directions">{t.contact.directions} →</a>
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="label border-b border-onyx pb-1 w-fit hover:opacity-60">{t.contact.follow} {CONTACT.instagramHandle} →</a>
            </div>

            <div className="mt-10 border-t border-onyx/15 pt-8">
              <p className="label text-terracotta">{t.contact.quick}</p>
              <div className="mt-4 flex flex-col gap-2 font-sans text-sm">
                <Link to="/faq" className="border-b border-onyx/20 w-fit pb-1 hover:border-onyx">{t.nav.faq}</Link>
                <Link to="/schedule" className="border-b border-onyx/20 w-fit pb-1 hover:border-onyx">{t.nav.schedule}</Link>
                <Link to="/pricing" className="border-b border-onyx/20 w-fit pb-1 hover:border-onyx">{t.common.threeForFiftyNine}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
